import React, { useState } from 'react';
import { Box, List, ListItemButton, ListItemText, IconButton, Typography, Button, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider, CircularProgress } from '@mui/material';
import { Add as AddIcon, MoreVert as MoreIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import type { ConversationSummary } from './useChatHistory';

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

interface ConversationSidebarProps {
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  isLoading: boolean;
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
  onRename: (id: string, title: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  disabled?: boolean;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  activeConversationId,
  isLoading,
  onSelectConversation,
  onNewChat,
  onRename,
  onDelete,
  disabled,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuConversationId, setMenuConversationId] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionConversationId, setActionConversationId] = useState<string | null>(null);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, id: string) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuConversationId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuConversationId(null);
  };

  const handleRenameClick = () => {
    const conversation = conversations.find((c) => c.id === menuConversationId);
    setActionConversationId(menuConversationId);
    setRenameTitle(conversation?.title || '');
    setRenameDialogOpen(true);
    handleMenuClose();
  };

  const handleRenameConfirm = async () => {
    if (actionConversationId && renameTitle.trim()) {
      await onRename(actionConversationId, renameTitle.trim());
    }
    setRenameDialogOpen(false);
    setActionConversationId(null);
    setRenameTitle('');
  };

  const handleDeleteClick = () => {
    setActionConversationId(menuConversationId);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (actionConversationId) {
      await onDelete(actionConversationId);
    }
    setDeleteDialogOpen(false);
    setActionConversationId(null);
  };

  return (
    <Box
      sx={{
        width: 280,
        display: 'flex',
        flexDirection: 'column',
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: 'grey.50',
        minHeight: 0,
      }}
    >
      {/* New Chat Button */}
      <Box sx={{ p: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          fullWidth
          onClick={onNewChat}
          disabled={disabled}
          size="small"
        >
          New Chat
        </Button>
      </Box>

      <Divider />

      {/* Conversation List */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : conversations.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No conversations yet
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {conversations.map((conv) => (
              <ListItemButton
                key={conv.id}
                selected={conv.id === activeConversationId}
                onClick={() => onSelectConversation(conv.id)}
                disabled={disabled}
                sx={{
                  py: 1,
                  px: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.50',
                    borderRight: 2,
                    borderColor: 'primary.main',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontWeight: conv.id === activeConversationId ? 600 : 400 }}
                    >
                      {conv.title || 'Untitled conversation'}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" component="span">
                      {formatRelativeTime(conv.updatedAt)}
                      {conv.messageCount > 0 && ` · ${conv.messageCount} msgs`}
                    </Typography>
                  }
                />
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, conv.id)}
                  disabled={disabled}
                  sx={{ ml: 0.5, opacity: 0.6, '&:hover': { opacity: 1 } }}
                >
                  <MoreIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleRenameClick}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename Conversation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Title"
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameConfirm();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameConfirm} variant="contained" disabled={!renameTitle.trim()}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs">
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this conversation? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConversationSidebar;
